import { useEffect, useState } from 'react';
import { useStore, selectActiveRun, selectPlayerHp } from '@/infra/store';
import { useT } from '@/infra/i18n';
import { getCombatRunService } from '@/services/combat-run';
import { COLORS, SPACING, RADIUS, FONT } from '@/presentation/theme/tokens';
import { ATTACK_COOLDOWN_MS, DODGE_COOLDOWN_MS } from '@/domain/expedition/types';
import { abilityForType } from '@/domain/combat/abilities';
import { MutationPickModal } from './MutationPickModal';
import { RunSummaryModal } from './RunSummaryModal';
import { VirtualJoystick } from '@/presentation/world/VirtualJoystick';
import { CombatIcon } from '@/presentation/icons/CombatIcon';
import { elementCombatIcon } from '@/domain/combat/combat-icons';
import { deriveCombatVisualProfile } from '@/domain/combat/combat-visual-profile';

export function ExpeditionHud() {
  const t = useT();
  const run = useStore(selectActiveRun);
  const hp = useStore(selectPlayerHp);
  const actionClock = useStore((s) => s.actionClock);
  const genome = useStore((s) => s.creature?.genome);
  const primaryType = genome?.primaryTypes[0] ?? 'Fire';
  const reducedMotion = useStore((s) => s.settings.accessibility.reducedMotion);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(timer);
  }, []);
  if (!run) return null;
  const ability = abilityForType(primaryType);
  const visualProfile = genome ? deriveCombatVisualProfile(genome) : null;
  const attackElapsed = now - actionClock.lastAttackAt;
  const dodgeElapsed = now - actionClock.lastDodgeAt;
  const skillElapsed = now - actionClock.lastSkillAt;
  const attackReady = attackElapsed >= ATTACK_COOLDOWN_MS;
  const dodgeReady = dodgeElapsed >= DODGE_COOLDOWN_MS;
  const skillReady = skillElapsed >= ability.cooldownMs;
  const dodgePct = Math.min(1, Math.max(0, dodgeElapsed / DODGE_COOLDOWN_MS));
  const skillPct = Math.min(1, Math.max(0, skillElapsed / ability.cooldownMs));
  const hpPct = Math.max(0, Math.min(1, hp.hp / Math.max(1, hp.max)));
  const hpLow = hpPct < 0.25;

  return (
    <>
      <div style={topLeft}>
        <div style={hpRow}>
          <span style={{ fontFamily: FONT.mono, fontSize: FONT.sizeSm, color: hpLow ? COLORS.danger : COLORS.text }}>
            <CombatIcon id="hp" size={16} label={t('combat.hp')} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t('combat.hp')} {Math.round(hp.hp)}/{Math.round(hp.max)}
          </span>
          <span style={{ fontFamily: FONT.mono, fontSize: FONT.sizeSm, color: COLORS.textDim }}>
            <CombatIcon id="kills" size={16} label={t('combat.kills')} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t('combat.kills')}: {run.enemiesKilled}
          </span>
        </div>
        <div style={hpTrack}>
          <div
            style={{
              width: `${hpPct * 100}%`,
              height: '100%',
              background: hpLow ? COLORS.danger : '#ff6e6e',
              transition: reducedMotion ? 'none' : 'width 0.2s',
              animation: hpLow && !reducedMotion ? 'pulse 0.6s ease-in-out infinite' : 'none',
            }}
          />
        </div>
        <div style={skillRow}>
          <div style={skillTrack}>
            <div
              style={{
                width: `${skillPct * 100}%`,
                height: '100%',
                background: skillReady ? '#88ccff' : '#3a4a6a',
                transition: reducedMotion ? 'none' : 'width 0.1s',
              }}
            />
          </div>
          <span style={skillLabel}>{skillReady ? `${primaryType} · ${t('combat.ready')}` : `${primaryType} · ${Math.ceil((ability.cooldownMs - skillElapsed) / 1000)}s`}</span>
        </div>
        <div style={hint}>{t('combat.controls')}</div>
      </div>

      <button
        data-testid="btn-extract"
        onClick={() => getCombatRunService().extract()}
        style={extractBtn}
      >
        <CombatIcon id="return" size={20} label={t('expedition.extract')} />
        {t('expedition.extract')}
      </button>

      <button
        data-testid="btn-skill-mobile"
        onClick={() => getCombatRunService().useSkill()}
        disabled={!skillReady}
        aria-label={`${t('combat.skill.aria')} ${(genome?.primaryTypes ?? [primaryType]).map((type) => t(`type.${type}`)).join(', ')}`}
        data-state={skillReady ? 'ready' : 'cooling'}
        style={{ ...skillBtn, ...cooldownStyle(skillPct, '#88ccff', skillReady) }}
      >
        <CombatIcon id={elementCombatIcon(primaryType)} size={34} label={`${t('combat.skill.aria')} ${primaryType}`} />
        {visualProfile?.secondaryBadges.map((id, index) => (
          <CombatIcon
            key={id}
            id={id}
            size={14}
            label=""
            style={{ position: 'absolute', left: -4 + index * 15, top: -5 }}
          />
        ))}
        <span style={keyBadge}>Q</span>
      </button>

      <button
        data-testid="btn-dodge-mobile"
        onClick={() => getCombatRunService().dodge(1, 0)}
        disabled={!dodgeReady}
        aria-label={t('combat.dodge.aria')}
        data-state={dodgeReady ? 'ready' : 'cooling'}
        style={{ ...dodgeBtn, ...cooldownStyle(dodgePct, '#f0c870', dodgeReady) }}
      >
        <CombatIcon id="dodge" size={34} label={t('combat.dodge.aria')} />
        <span style={keyBadge}>L</span>
      </button>

      <button
        data-testid="btn-attack-mobile"
        onClick={() => mobileAttack()}
        disabled={!attackReady}
        aria-label={t('combat.attack.aria')}
        data-state={attackReady ? 'ready' : 'cooling'}
        style={{ ...attackBtn, opacity: attackReady ? 1 : 0.55 }}
      >
        <CombatIcon id="attack" size={38} label={t('combat.attack.aria')} />
        <span style={keyBadge}>J</span>
      </button>

      <VirtualJoystick />
      <MutationPickModal />
      <RunSummaryModal />
      <style>{`@keyframes pulse {0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </>
  );
}

function mobileAttack(): void {
  getCombatRunService().attack(1, 0);
}

const topLeft: React.CSSProperties = {
  position: 'fixed',
  top: 12,
  left: 12,
  background: COLORS.surfaceSoft,
  padding: SPACING.md,
  borderRadius: RADIUS.lg,
  border: `1px solid ${COLORS.border}`,
  zIndex: 90,
  minWidth: 240,
  backdropFilter: 'blur(6px)',
};
const hpRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACING.xs,
};
const hpTrack: React.CSSProperties = {
  height: 10,
  background: '#222',
  borderRadius: RADIUS.sm,
  overflow: 'hidden',
  border: `1px solid ${COLORS.border}`,
  marginBottom: SPACING.xs,
};
const skillRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: SPACING.sm,
};
const skillTrack: React.CSSProperties = {
  flex: 1,
  height: 6,
  background: '#222',
  borderRadius: RADIUS.sm,
  overflow: 'hidden',
};
const skillLabel: React.CSSProperties = {
  fontSize: FONT.sizeXs,
  fontFamily: FONT.mono,
  color: COLORS.textDim,
};
const hint: React.CSSProperties = {
  marginTop: SPACING.xs,
  fontSize: FONT.sizeXs,
  fontFamily: FONT.mono,
  color: COLORS.textFaint,
};
const extractBtn: React.CSSProperties = {
  position: 'fixed',
  top: 12,
  right: 12,
  background: COLORS.surfaceSoft,
  color: COLORS.warn,
  border: `1px solid ${COLORS.warn}88`,
  borderRadius: RADIUS.md,
  padding: `${SPACING.sm}px ${SPACING.md}px`,
  cursor: 'pointer',
  fontSize: FONT.sizeSm,
  zIndex: 90,
  backdropFilter: 'blur(6px)',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
const skillBtn: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 100,
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: 'rgba(60,160,255,0.5)',
  border: '2px solid #88ccff',
  cursor: 'pointer',
  zIndex: 95,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const attackBtn: React.CSSProperties = {
  position: 'fixed',
  right: 16,
  bottom: 24,
  width: 64,
  height: 64,
  borderRadius: '50%',
  background: 'rgba(255,80,80,0.5)',
  border: '2px solid #ff6e6e',
  cursor: 'pointer',
  zIndex: 95,
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const dodgeBtn: React.CSSProperties = {
  ...skillBtn,
  right: 92,
  bottom: 28,
  width: 56,
  height: 56,
  borderColor: '#f0c870',
};
const keyBadge: React.CSSProperties = {
  position: 'absolute',
  right: -3,
  bottom: -3,
  minWidth: 18,
  height: 18,
  padding: '0 3px',
  border: '1px solid #fff2',
  borderRadius: 4,
  background: '#151522',
  color: '#fff4d6',
  fontFamily: FONT.mono,
  fontSize: 10,
  lineHeight: '16px',
  textAlign: 'center',
};

function cooldownStyle(progress: number, accent: string, ready: boolean): React.CSSProperties {
  return {
    background: ready
      ? `rgba(30, 36, 52, 0.92)`
      : `conic-gradient(${accent} ${Math.round(progress * 360)}deg, rgba(18,18,28,0.92) 0)`,
    opacity: ready ? 1 : 0.72,
    cursor: ready ? 'pointer' : 'not-allowed',
  };
}
